/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CacheEntry} from '@adobe/exc-app/cache';
import DataPrefetchContract, {DataDedupConfig, StandardVariables} from '@adobe/exc-app/network/DataPrefetchContract';
import {UserOrgContext} from '../models/Context';

export interface FulfillableData {
  region: string;
  environment: string;
}

/**
 * Returns the ACP fullfilable data record. This is used to allow the sandbox switcher to run on QA
 * against stage orgs.
 * @param environment
 * @param imsOrg - IMS Org ID
 * @param projectedProductContext - ACP Product Context
 * @returns Fulfillable data record if available and needed.
 */
const getAcpFulfillableData = ({environment, imsOrg, imsProfile: {projectedProductContext}}: UserOrgContext): FulfillableData | undefined => {
  let result = undefined;
  if (environment === 'dev' || environment === 'qa') {
    const {prodCtx: acpPC} = projectedProductContext.find(({prodCtx}) =>
      prodCtx.serviceCode === 'acp' &&
      prodCtx.owningEntity === imsOrg &&
      prodCtx.fulfillable_data
    ) || {};
    if (acpPC) {
      result = JSON.parse(acpPC.fulfillable_data as string) as FulfillableData;
    }
  }
  return result;
};

const getArray = <T>(data: T, arrayElement: string): Array<any> => {
  const array = (data as any)[arrayElement];
  if (array && Array.isArray(array)) {
    return array;
  }
  return [];
};

export function mergeValuesByUniqueId<T>(fresh: T, cached: T, dedup: DataDedupConfig): T {
  const {arrayElement, keyField, sortAscending, sortField} = dedup;
  const keys = new Set<string>();
  let newArr: Array<any> = [];
  for (const element of [...getArray<T>(fresh, arrayElement), ...getArray<T>(cached, arrayElement)]) {
    const key = element[keyField] as string;
    if (!keys.has(key)) {
      newArr.push(element);
      keys.add(key);
    }
  }

  const cmp = (a: string|number, b: string|number): number => {
    if (typeof a === 'string' && typeof b === 'string') {
      return sortAscending ? a.localeCompare(b) : b.localeCompare(a);
    }

    return sortAscending ? Number(a) - Number(b) : Number(b) - Number(a);
  };

  if (sortField && newArr.length > 1) {
    // Sort the array.
    newArr = newArr.sort((a, b) => cmp(a[sortField], b[sortField]));
  }

  if (dedup.arrayLimit && newArr.length > dedup.arrayLimit) {
    newArr = newArr.splice(0, dedup.arrayLimit);
  }

  return {...fresh, [arrayElement]: newArr};
}

/**
 * If variable comes from cache value, return specific value from cache entry
 * @template T
 * @param cached - object in cache
 * @param contract
 * @returns value of variable field from cached item
 */
function getCacheVariable<T>(cached: CacheEntry<T>, dedup: DataDedupConfig) {
  if (cached?.value) {
    const {arrayElement, sortField} = dedup;
    const entry = [...getArray<T>(cached.value, arrayElement)];
    return entry.length > 0 ? +entry[0][sortField] : cached.createdAt;
  }
  return cached.createdAt;
}

/**
 * @returns transformed value from variable key string
 */
function getTransformedValue<T>(variableKey: string, context: UserOrgContext, cached?: CacheEntry<T>, contract?: DataPrefetchContract<T>) {
  switch (variableKey) {
    case StandardVariables.ACP_FD:
      return getAcpFulfillableData(context);
    case StandardVariables.CACHED_TS:
      if (!cached) {
        throw new Error(`Cached data required but not available ${variableKey}`);
      }
      return cached.createdAt;
    case StandardVariables.DEDUP_SF:
      if (!cached || !contract?.refreshData?.dedup) {
        throw new Error(`Cached data is not available or dedup config is not defined. Both are required ${variableKey}`);
      }
      return getCacheVariable(cached, contract.refreshData.dedup);
  }
}

/**
 * Computes any GraphQL variables that are required dynamically.
 * @template T
 * @param variables - GraphQL variables payload as defined in the data contract
 * @param context - Core Context
 * @param cached - Cached data (Optional)
 */
export function transformVariables<T>(
  variables: Record<string, any> | undefined,
  context: UserOrgContext,
  cached?: CacheEntry<T>,
  contract?: DataPrefetchContract<T>): Record<string, any> {
  const transformed: Record<string, any> = {};
  if (variables) {
    Object.entries(variables).forEach(([key, value]) => {
      if (typeof value === 'string' && value.startsWith('${') && value.endsWith('}')) {
        // This value needs to be transformed, remove ${} first
        const variableKey = value.slice(2, value.length - 1);
        transformed[key] = getTransformedValue<T>(variableKey, context, cached, contract);
      } else {
        transformed[key] = value;
      }
    });
  }
  return transformed;
}
